import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { api } from "../../lib/api";
import { allTexts, applyLabels, builtinText, type Labels } from "../../lib/labels";
import { Empty, SectionHeader } from "../ui/Badges";
import { IntInput, Switch } from "../catalog/fields";

interface Rank { code: string; name: string; nameAr: string }
interface Ops {
  values: Record<string, number | string | Rank[]>;
  defaults: Record<string, number | string | Rank[]>;
  limits: Record<string, { def: number; min: number; max: number }>;
  branchOverrides: string[];
}
const NUMERIC = ["warn_minutes", "extension_minutes", "grace_minutes", "overtime_fils_per_15m", "loyalty_stamps", "scan_hold_minutes", "wallet_fils_per_credit", "party_extra_kid_fils"] as const;
const MONEY = new Set(["overtime_fils_per_15m", "wallet_fils_per_credit", "party_extra_kid_fils"]);

/** Operations: the numbers the floor runs on, per branch or for every branch, plus the kids' rank list. */
export function OperationsPanel() {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["setup", "operations"], queryFn: () => api.get<Ops>("/setup/operations") });
  const [draft, setDraft] = useState<Record<string, number | string | Rank[]>>({});
  const [scope, setScope] = useState<"branch" | "company">("branch");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (q.data) setDraft({}); }, [q.data]);
  if (!q.data) return <Empty>{t("common.loading")}</Empty>;
  const ops = q.data;
  const val = (k: string) => (k in draft ? draft[k]! : ops.values[k]!);
  const ranks = val("kid_ranks") as Rank[];
  const dirty = Object.keys(draft).length > 0;
  const set = (k: string, v: number | string | Rank[]) => setDraft((d) => ({ ...d, [k]: v }));
  const rankOk = ranks.length > 0 && ranks.every((r) => /^[a-z0-9_]{2,30}$/.test(r.code) && r.name.trim() && r.nameAr.trim()) && new Set(ranks.map((r) => r.code)).size === ranks.length;

  async function save(body: { values?: Record<string, unknown>; reset?: string[] }) {
    setBusy(true);
    try {
      await api.put("/setup/operations", { scope, values: {}, ...body });
      await Promise.all([qc.invalidateQueries({ queryKey: ["setup", "operations"] }), qc.invalidateQueries({ queryKey: ["sessions"] })]);
      setDraft({});
      toast(t("setup.saved"), "success");
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card space-y-4 p-4 sm:p-5" aria-label={t("setup.tabs.operations")}>
      <SectionHeader title={`⚙️ ${t("setup.tabs.operations")}`} subtitle={t("setup.ops.subtitle")}
        right={isAdmin ? (
          <div className="flex gap-1 text-[11px] font-black" role="radiogroup" aria-label={t("setup.ops.scope")}>
            {(["branch", "company"] as const).map((s) => <button key={s} type="button" role="radio" aria-checked={scope === s} onClick={() => setScope(s)} className={`rounded-lg px-2.5 py-1 ${scope === s ? "bg-cosmic-navy text-white" : "bg-gray-100 text-gray-700"}`}>{t(`setup.ops.scope_${s}`)}</button>)}
          </div>
        ) : undefined} />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {NUMERIC.map((k) => {
          const lim = ops.limits[k]!;
          const own = ops.branchOverrides.includes(k);
          const money = MONEY.has(k);
          return (
            <div key={k} className="rounded-2xl border border-gray-200 bg-gray-50/60 p-3" data-setting={k}>
              <label className="label" htmlFor={`op-${k}`}>{t(`setup.ops.${k}.title`)}</label>
              <div className="flex items-center gap-2">
                <div className="w-32"><IntInput id={`op-${k}`} value={Number(val(k))} min={lim.min} max={lim.max} onChange={(n) => set(k, n)} /></div>
                <span className="text-[11px] font-bold text-gray-500">{money ? t("setup.ops.fils") : t(`setup.ops.${k}.unit`)}</span>
                {own && scope === "branch" && <button type="button" className="btn-ghost !px-2 !py-0.5 text-[10px]" onClick={() => void save({ reset: [k] })}>{t("setup.ops.useCompany")}</button>}
              </div>
              <p className="mt-1 text-[10px] font-semibold text-gray-500">{t(`setup.ops.${k}.help`)}{own ? ` · ${t("setup.ops.ownValue")}` : ""}</p>
            </div>
          );
        })}
      </div>

      <fieldset className="space-y-2 rounded-2xl border border-gray-200 p-3">
        <legend className="px-1 text-[11px] font-black text-gray-600">{t("setup.ops.ranks")}</legend>
        <p className="text-[11px] font-semibold text-gray-500">{t("setup.ops.ranksHelp")}</p>
        {ranks.map((r, i) => (
          <div key={i} className="grid grid-cols-12 items-end gap-2" data-rank={i + 1}>
            <div className="col-span-3"><label className="label" htmlFor={`rk-code-${i}`}>{t("setup.ops.rankCode")}</label><input id={`rk-code-${i}`} className="field num font-mono" value={r.code} onChange={(e) => set("kid_ranks", ranks.map((x, k) => (k === i ? { ...x, code: e.target.value.toLowerCase() } : x)))} /></div>
            <div className="col-span-4"><label className="label" htmlFor={`rk-en-${i}`}>{t("setup.name")}</label><input id={`rk-en-${i}`} className="field" value={r.name} onChange={(e) => set("kid_ranks", ranks.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)))} /></div>
            <div className="col-span-4"><label className="label" htmlFor={`rk-ar-${i}`}>{t("setup.nameAr")}</label><input id={`rk-ar-${i}`} className="field" dir="rtl" value={r.nameAr} onChange={(e) => set("kid_ranks", ranks.map((x, k) => (k === i ? { ...x, nameAr: e.target.value } : x)))} /></div>
            <button type="button" className="btn-ghost col-span-1 !px-2 text-red-600" aria-label={t("setup.ops.removeRank", { n: i + 1 })} disabled={ranks.length <= 1} onClick={() => set("kid_ranks", ranks.filter((_, k) => k !== i))}>🗑</button>
          </div>
        ))}
        {ranks.length < 12 && <button type="button" className="btn-ghost text-xs" onClick={() => set("kid_ranks", [...ranks, { code: `rank_${ranks.length + 1}`, name: "", nameAr: "" }])}>＋ {t("setup.ops.addRank")}</button>}
      </fieldset>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-primary" disabled={!dirty || busy || ("kid_ranks" in draft && !rankOk)} onClick={() => void save({ values: draft })}>{t("setup.save")}</button>
        {dirty && <button type="button" className="btn-ghost" onClick={() => setDraft({})}>{t("setup.ops.discard")}</button>}
        <span className="text-[11px] font-semibold text-gray-500">{scope === "company" ? t("setup.ops.company_note") : t("setup.ops.branch_note")}</span>
      </div>
    </section>
  );
}

/** Wording: rename any text on any screen, per language. Placeholders such as {{count}} must stay. */
export function WordingPanel() {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const saved = useQuery({ queryKey: ["setup", "labels"], queryFn: () => api.get<Labels>("/setup/labels") });
  const [term, setTerm] = useState("");
  const [edits, setEdits] = useState<{ ar: Record<string, string>; en: Record<string, string> }>({ ar: {}, en: {} });
  const [busy, setBusy] = useState(false);
  const texts = useMemo(() => ({ en: new Map(allTexts("en")), ar: new Map(allTexts("ar")) }), [saved.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const keys = useMemo(() => [...new Set([...texts.en.keys(), ...texts.ar.keys()])].filter((k) => !k.startsWith("errors.") && !k.startsWith("coupon.reason.")), [texts]);

  const effective = (lng: "ar" | "en", key: string) => edits[lng][key] ?? saved.data?.[lng]?.[key] ?? texts[lng].get(key) ?? "";
  const needle = term.trim().toLowerCase();
  const rows = needle.length < 2 ? [] : keys.filter((k) => k.toLowerCase().includes(needle) || (texts.en.get(k) ?? "").toLowerCase().includes(needle) || (texts.ar.get(k) ?? "").includes(term.trim()) || effective("ar", k).includes(term.trim()) || effective("en", k).toLowerCase().includes(needle)).slice(0, 40);
  const holes = (s: string) => (s.match(/\{\{\s*\w+\s*\}\}/g) ?? []).map((x) => x.replace(/\s/g, "")).sort().join(",");
  const bad = (lng: "ar" | "en", key: string) => key in edits[lng] && holes(edits[lng][key]!) !== holes(texts[lng].get(key) ?? "");
  const anyBad = (["ar", "en"] as const).some((l) => Object.keys(edits[l]).some((k) => bad(l, k)));
  const dirty = Object.keys(edits.ar).length + Object.keys(edits.en).length > 0;
  const overridden = (lng: "ar" | "en", key: string) => (edits[lng][key] ?? saved.data?.[lng]?.[key]) !== undefined && effective(lng, key) !== (texts[lng].get(key) ?? "");

  async function save() {
    const next: Required<Labels> = { ar: { ...(saved.data?.ar ?? {}) }, en: { ...(saved.data?.en ?? {}) } };
    for (const lng of ["ar", "en"] as const) {
      for (const [k, v] of Object.entries(edits[lng])) {
        if (v.trim() === "" || v === texts[lng].get(k)) delete next[lng][k]; // empty or identical to the built-in wording = no override
        else next[lng][k] = v.trim();
      }
    }
    setBusy(true);
    try {
      await api.put("/setup/labels", next);
      applyLabels(next);
      setEdits({ ar: {}, en: {} });
      await qc.invalidateQueries({ queryKey: ["setup", "labels"] });
      toast(t("setup.saved"), "success");
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }
  async function reset(key: string) {
    const next: Required<Labels> = { ar: { ...(saved.data?.ar ?? {}) }, en: { ...(saved.data?.en ?? {}) } };
    delete next.ar[key]; delete next.en[key];
    setEdits((e) => { const a = { ...e.ar }, b = { ...e.en }; delete a[key]; delete b[key]; return { ar: a, en: b }; });
    try {
      await api.put("/setup/labels", next);
      applyLabels(next);
      await qc.invalidateQueries({ queryKey: ["setup", "labels"] });
    } catch (e) {
      toastError(e);
    }
  }
  const count = Object.keys(saved.data?.ar ?? {}).length + Object.keys(saved.data?.en ?? {}).length;

  return (
    <section className="card space-y-3 p-4 sm:p-5" aria-label={t("setup.tabs.wording")}>
      <SectionHeader title={`✍️ ${t("setup.tabs.wording")}`} subtitle={t("setup.wording.subtitle")} />
      <div className="flex flex-wrap items-center gap-3">
        <input className="field !w-72" aria-label={t("setup.wording.search")} placeholder={t("setup.wording.search")} value={term} onChange={(e) => setTerm(e.target.value)} />
        <span className="text-[11px] font-bold text-gray-500">{t("setup.wording.count", { count })}</span>
        <span className="flex-1" />
        <button type="button" className="btn-primary" disabled={!dirty || anyBad || busy} onClick={() => void save()}>{t("setup.save")}</button>
      </div>
      {needle.length < 2 ? <Empty>{t("setup.wording.hint")}</Empty> : rows.length === 0 ? <Empty>{t("setup.wording.none")}</Empty> : (
        <div className="space-y-2">
          {rows.map((k) => (
            <div key={k} className="rounded-2xl border border-gray-200 bg-gray-50/60 p-3" data-key={k}>
              <div className="mb-1.5 flex items-center justify-between"><span className="num font-mono text-[10px] text-gray-400">{k}</span>
                {(overridden("ar", k) || overridden("en", k)) && <button type="button" className="btn-ghost !px-2 !py-0.5 text-[10px]" onClick={() => void reset(k)}>↩ {t("setup.wording.reset")}</button>}</div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {(["en", "ar"] as const).map((lng) => (
                  <div key={lng}>
                    <label className="label" htmlFor={`wd-${lng}-${k}`}>{lng === "en" ? "English" : "العربية"}</label>
                    <input id={`wd-${lng}-${k}`} className={`field ${bad(lng, k) ? "!border-red-400" : ""}`} dir={lng === "ar" ? "rtl" : "ltr"} value={effective(lng, k)} maxLength={300} onChange={(e) => setEdits((x) => ({ ...x, [lng]: { ...x[lng], [k]: e.target.value } }))} />
                    {bad(lng, k) && <p className="text-[10px] font-bold text-red-600">{t("setup.wording.placeholders", { list: (texts[lng].get(k)?.match(/\{\{\s*\w+\s*\}\}/g) ?? []).join(" ") })}</p>}
                    {overridden(lng, k) && <p className="text-[10px] font-semibold text-gray-400">{t("setup.wording.was")}: {builtinText(lng, k)}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <Switch checked disabled onChange={() => undefined}>{t("setup.wording.live")}</Switch>
    </section>
  );
}
